import { readonly, ref } from 'vue';
import { Theme } from './types';

const THEME_KEY = 'theme';
const THEME_DEFAULT = Theme.SYSTEM;
const THEME_MEDIA_QUERY_DARK = '(prefers-color-scheme: dark)';
const THEME_DATA_ATTRIBUTE = 'data-theme';

const currentTheme = ref<Theme | null>(null); 

export const useTheme = () => {
  const themeMediaQuery = window.matchMedia(THEME_MEDIA_QUERY_DARK);

  const getTheme = () => {
    const themeFromLocalStorage = localStorage.getItem(THEME_KEY) as Theme;

    return themeFromLocalStorage || THEME_DEFAULT;
  }
  
  const getSystemTheme = () => themeMediaQuery.matches ? Theme.DARK : Theme.LIGHT;
  
  const setTheme = (theme: Theme, colorTheme?: Theme) => {
    currentTheme.value = theme;
    localStorage.setItem(THEME_KEY, theme);
    document.documentElement.setAttribute(THEME_DATA_ATTRIBUTE, colorTheme || theme);
  };
  
  const setThemeLight = () => setTheme(Theme.LIGHT);
  
  const setThemeDark = () => setTheme(Theme.DARK);
  
  const setThemeSystem = () => {
    const theme = getSystemTheme();

    setTheme(Theme.SYSTEM, theme);
  };
  
  const initialTheme = () => {
    const theme = getTheme();
    const systemTheme = getSystemTheme();

    if (theme === Theme.SYSTEM) {
      setTheme(Theme.SYSTEM, systemTheme);
    } else {
      setTheme(theme);
    }
  };
  
  const changeThemeByEvent = (event: MediaQueryListEvent) => {
    if (event.matches) {
      setTheme(Theme.SYSTEM, Theme.DARK);
    } else {
      setTheme(Theme.SYSTEM, Theme.LIGHT);
    }
  };
  
  const addThemeMediaQueryListener = () => {
    themeMediaQuery.addEventListener('change', changeThemeByEvent);
  };

  const removeThemeMediaQueryListener = () => {
    themeMediaQuery.removeEventListener('change', changeThemeByEvent);
  };

  return {
    setThemeLight,
    setThemeDark,
    setThemeSystem,
    initialTheme,
    getTheme,
    currentTheme: readonly(currentTheme),
    addThemeMediaQueryListener,
    removeThemeMediaQueryListener,
  };
}
